import { Close, Done } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { SubmitIcon } from '../../../../vectors/index';
import { FC, useContext, useState } from 'react';
import useUpateCompanyPlan from '@features/payments/hooks/useUpdateCompanyPlan';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PLANS } from '@utils/pricing';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';

type BulkSwitchPlanModalProps = {
  isOpen: boolean;
  hasPaymentMethod: boolean;
  planIds: { company_id?: number; plan_id?: number; new_plan_id: number }[];
  handleClose: () => void;
};

const BulkSwitchPlanModal: FC<BulkSwitchPlanModalProps> = ({
  isOpen,
  planIds,
  handleClose,
  hasPaymentMethod,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(authContext);
  const billingCycle = authData?.user?.billing_cycle;
  const [errorText, setErrorText] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('4');
  const { updatePlan, isUpdatingPlan } = useUpateCompanyPlan({ setErrorText });
  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });
  const handleSubmit = () => {
    if (!hasPaymentMethod) {
      return navigate('#Payment-Info');
    }
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status == 200) {
          setErrorText('');
          toast.success(data.message);
          handleClose();
        }
      },
    };

    updatePaymentPlan(
      {
        billing_cycle: billingCycle ?? undefined,
        upgrade_plan: false,
        downgrade_plan: false,
        switch: false,
        plan_ids: planIds.map((item)=>{
          return {
            company_id: item.company_id,
            plan_id: item.plan_id,
            new_plan_id: Number(selectedPlan),
          }
        })
      },
      options,
    );
  };

  return (
    <>
      <Dialog
        open={Boolean(isOpen)}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <SubmitIcon />
          <br />
          <Typography variant="h4" mt={4}>
            Are you sure you want to move the selected companies to a new plan?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl component="fieldset">
              <FormLabel>Select plan</FormLabel>
              <RadioGroup
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                row
              >
                <FormControlLabel
                  value="4"
                  control={<Radio />}
                  label={PLANS.STARTER.NAME}
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={PLANS.SMARTER.NAME}
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label={PLANS.PRO.NAME}
                />
              </RadioGroup>
            </FormControl>
            {errorText && <Alert severity="error">{errorText}</Alert>}
            <LoadingButton
              variant="contained"
              startIcon={<Done />}
              onClick={handleSubmit}
              loading={isUpdatingPlan || isUpdatingPaymentPlan}
            >
              Confirm
            </LoadingButton>
            <Button
              startIcon={<Close />}
              variant="outlined"
              color="secondary"
              onClick={handleClose}
              disabled={isUpdatingPlan || isUpdatingPaymentPlan}
            >
              Cancel
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkSwitchPlanModal;
